<template>
  <base-dropdown
    ref="dropdownRef"
    v-if="0 < reportsList.length"
    menu-on-right
    class="nav-item"
    tag="li"
    title-tag="a"
    title-classes="nav-link pr-0"
    @change="onDropdownToggle"
  >
    <base-button
      type="white"
      class="nav-link px-2 px-sm-3 m-0 font-weight-normal d-flex align-items-center justify-content-between"
      size="sm"
      slot="title-container"
      outline
    >
      <span>{{ selectedName || 'My Reports' }}</span>
      <b-icon
        icon="chevron-down"
        font-scale="0.8"
        class="ml-2 ml-md-3 align-middle"
      ></b-icon>
    </base-button>

    <li class="reports-dropdown__search-wrapper" @click.stop>
      <div class="reports-dropdown__search-container">
        <b-form-input
          ref="searchInputRef"
          v-model="searchQuery"
          placeholder="Search reports…"
          size="sm"
          autocomplete="off"
          class="reports-dropdown__search-input"
          @keydown="onSearchKeydown"
        />
        <button
          v-if="searchQuery"
          type="button"
          class="reports-dropdown__clear-btn"
          @click.prevent.stop="clearSearch"
          aria-label="Clear search"
        >
          ✕
        </button>
      </div>
    </li>

    <template v-if="filteredList.length">
      <b-dropdown-item
        v-for="report in filteredList"
        :key="`report-${report.id}`"
        :disabled="activeReport == (report.report_id || report.id)"
        :to="{
          name: 'Dashboard',
          params: { report_id: encodeReportID(report.report_id || report.id) }
        }"
        link-class="d-flex align-items-center"
      >
        <b-icon
          @click.prevent.stop="onFavorite(report)"
          :icon="report.is_favorite ? 'star-fill' : 'star'"
          font-scale="1"
          class="mr-2"
        ></b-icon>
        <span class="reports-dropdown__item-name">
          <template v-for="(part, index) in getHighlightedParts(reportNameByRole(report))">
            <mark
              v-if="part.matched"
              :key="`m-${index}`"
              class="reports-dropdown__highlight"
              >{{ part.text }}</mark
            ><span v-else :key="`s-${index}`">{{ part.text }}</span>
          </template>
        </span>
      </b-dropdown-item>
    </template>

    <li v-if="searchQuery && !filteredList.length" class="reports-dropdown__empty-state">
      <small>No reports match your search</small>
    </li>
  </base-dropdown>
</template>

<script>
import { orderBy } from 'lodash-es'
import { userMixin, reportMixin } from '@mixins'
import { ReportsService } from '@services'

export default {
  name: 'reports-dropdown',
  props: {
    activeReport: {
      default: null
    },
    // Optional: when a parent already fetched the reports list (e.g. AppNavbar),
    // pass it in here to skip this component's own fetch. Falls back to
    // self-fetching when omitted, so existing standalone usages (e.g.
    // ExportOld.vue) are unaffected.
    reports: {
      type: Array,
      default: null
    }
  },
  mixins: [userMixin, reportMixin],
  data() {
    return {
      reportsList: [],
      searchQuery: ''
    }
  },
  computed: {
    routeAndReportName() {
      return {
        route: this.$route.name,
        report: this.selectedName
      }
    },
    selectedName() {
      if (!this.reportsList.length && !this.activeReport) return null

      const index = this.reportsList.findIndex(
        (item) => item.report_id === this.activeReport
      )
      const selectedReport = 0 <= index ? this.reportsList[index] : null

      return selectedReport ? this.reportNameByRole(selectedReport) : null
    },
    filteredList() {
      if (!this.searchQuery) return this.reportsList

      const query = this.searchQuery.toLowerCase()
      return this.reportsList.filter((report) => {
        const name = this.reportNameByRole(report)
        return name.toLowerCase().includes(query)
      })
    }
  },
  methods: {
    sortList() {
      if (!this.reportsList.length) return false

      const nameSort = (item) =>
        item.display_name
          ? item.display_name.toLowerCase()
          : item.name.toLowerCase()

      this.reportsList = orderBy(
        this.reportsList,
        ['is_favorite', nameSort],
        ['desc', 'asc']
      )
    },
    async fetchReports() {
      const rs = await ReportsService.list({ page_size: 1000 }, false)

      this.reportsList = rs.data.data.data

      this.sortList()
    },
    async onFavorite(report) {
      await ReportsService.favorite(
        {
          user_id: this.currentUser.id,
          report_id: report.report_id
        },
        report.is_favorite ? 'remove' : 'set'
      )

      const reportIndex = this.reportsList.findIndex(
        (item) => item.report_id === report.report_id
      )
      if (0 <= reportIndex)
        this.reportsList[reportIndex].is_favorite = report.is_favorite
          ? false
          : true

      this.sortList()
    },
    onDropdownToggle(isOpen) {
      if (!isOpen) {
        this.searchQuery = ''
        return
      }

      // BaseDropdown can close without emitting `change` (e.g. its delayed close path),
      // so clear on open as well to avoid stale filters.
      this.searchQuery = ''
      this.$nextTick(() => {
        if (this.$refs.searchInputRef) {
          this.$refs.searchInputRef.focus()
        }
      })
    },
    onSearchKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        if (this.searchQuery) {
          this.searchQuery = ''
        } else {
          if (this.$refs.dropdownRef) {
            this.$refs.dropdownRef.closeDropDown()
          }
        }
      }
    },
    clearSearch() {
      this.searchQuery = ''
      this.$nextTick(() => {
        if (this.$refs.searchInputRef) {
          this.$refs.searchInputRef.focus()
        }
      })
    },
    getHighlightedParts(name) {
      if (!this.searchQuery) return [{ text: name, matched: false }]

      const query = this.searchQuery.toLowerCase()
      const nameLower = name.toLowerCase()
      const parts = []
      let lastIndex = 0
      let matchIndex = nameLower.indexOf(query)

      while (-1 !== matchIndex) {
        if (matchIndex > lastIndex) {
          parts.push({ text: name.slice(lastIndex, matchIndex), matched: false })
        }
        parts.push({
          text: name.slice(matchIndex, matchIndex + query.length),
          matched: true
        })
        lastIndex = matchIndex + query.length
        matchIndex = nameLower.indexOf(query, lastIndex)
      }

      if (lastIndex < name.length) {
        parts.push({ text: name.slice(lastIndex), matched: false })
      }

      return parts
    }
  },
  watch: {
    routeAndReportName: {
      deep: true,
      immediate: true,
      handler(newVal) {
        if ('ReportView' === newVal.route && newVal.report)
          this.setPageTitle(newVal.report)
      }
    },
    // `reports` is bound (even to a still-loading empty array) by callers that
    // share a single fetch across multiple dropdowns — sync to it instead of
    // self-fetching once it resolves. Unbound callers get `null` and never hit
    // this, relying on `fetchReports()` from `mounted()` below instead.
    reports(newVal) {
      if (newVal) {
        this.reportsList = newVal
        this.sortList()
      }
    }
  },
  mounted() {
    if (this.reports) {
      this.reportsList = this.reports
      this.sortList()
    } else {
      this.fetchReports()
    }
  }
}
</script>

<style lang="scss" scoped>
.reports-dropdown {
  &__search-wrapper {
    padding: 0.5rem 0.75rem 0.375rem;
    list-style: none;
  }

  &__search-container {
    position: relative;
    display: flex;
    align-items: center;
  }

  &__search-input {
    padding-right: 1.75rem !important;
  }

  &__clear-btn {
    position: absolute;
    right: 0.375rem;
    background: none;
    border: none;
    padding: 0;
    line-height: 1;
    font-size: 0.75rem;
    color: #6c757d;
    cursor: pointer;

    &:hover {
      color: #343a40;
    }
  }

  &__highlight {
    background-color: rgba(255, 193, 7, 0.4);
    padding: 0;
    border-radius: 2px;
  }

  &__empty-state {
    list-style: none;
    padding: 0.5rem 1rem;
    color: #6c757d;
  }
}
</style>
